import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  BarChart3, 
  Users, 
  Cloud, 
  TrendingUp, 
  DollarSign, 
  Shield, 
  Zap,
  ArrowRight,
  Settings,
  Search
} from 'lucide-react';
import '../../styles/ServiceRichText.css';

interface ServiceRichTextProps {
  title?: string;
  content: string;
  variant?: 'default' | 'highlighted' | 'compact';
}

const ServiceRichText: React.FC<ServiceRichTextProps> = ({ 
  title, 
  content, 
  variant = 'default' 
}) => {
  const [countUp, setCountUp] = useState({ savings: 0, visibility: 0, efficiency: 0 });
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setCountUp({ savings: 40, visibility: 95, efficiency: 60 });
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const renderRichText = () => {
    const features = [
      {
        icon: <BarChart3 size={16} />,
        text: "Real-time cost visibility across multi-cloud environments"
      },
      {
        icon: <Users size={16} />,
        text: "Team-level cost ownership and attribution"
      },
      {
        icon: <Shield size={16} />,
        text: "Automated governance and policy enforcement"
      },
      {
        icon: <Settings size={16} />,
        text: "Developer-integrated cost awareness tools"
      }
    ];

    return (
      <div className="bq-rich-text-container">
        <div className="bq-rich-text-inner">
          {/* Header section with badge */}
          <div className="bq-rich-header">
            <div className="bq-title-badge">
              <Search size={16} className="bq-title-icon" />
              <span>FinOps Solution</span>
            </div>
            
            <h1 className="bq-rich-title">
              Struggling with Rising
              <br />
              <span className="bq-rich-title-underline">Cloud Costs?</span>
            </h1>
            
            <p className="bq-rich-subtitle">
              We Help You Take Control
            </p>
          </div>

          {/* Stats section */}
          <div className="bq-stats-container">
            <div className="bq-stat-card">
              <div className="bq-stat-icon-wrapper bq-stat-icon-green">
                <TrendingUp size={18} className="bq-stat-icon" />
              </div>
              
              <div className="bq-stat-value bq-stat-value-green">
                {countUp.savings}<span className="bq-stat-percent">%</span>
              </div>
              
              <div className="bq-progress-bar">
                <div 
                  className="bq-progress-fill bq-progress-fill-green" 
                  style={{ width: `${countUp.savings}%` }}
                ></div>
              </div>
              
              <div className="bq-stat-label">Average Cost Reduction</div>
            </div>
            
            <div className="bq-stat-card">
              <div className="bq-stat-icon-wrapper bq-stat-icon-blue">
                <BarChart3 size={18} className="bq-stat-icon" />
              </div>
              
              <div className="bq-stat-value bq-stat-value-blue">
                {countUp.visibility}<span className="bq-stat-percent">%</span>
              </div>
              
              <div className="bq-progress-bar">
                <div 
                  className="bq-progress-fill bq-progress-fill-blue" 
                  style={{ width: `${countUp.visibility}%` }}
                ></div>
              </div>
              
              <div className="bq-stat-label">Cost Visibility Increase</div>
            </div>
            
            <div className="bq-stat-card">
              <div className="bq-stat-icon-wrapper bq-stat-icon-purple">
                <Zap size={18} className="bq-stat-icon" />
              </div>
              
              <div className="bq-stat-value bq-stat-value-purple">
                {countUp.efficiency}<span className="bq-stat-percent">%</span>
              </div>
              
              <div className="bq-progress-bar">
                <div 
                  className="bq-progress-fill bq-progress-fill-purple" 
                  style={{ width: `${countUp.efficiency}%` }}
                ></div>
              </div>
              
              <div className="bq-stat-label">Resource Efficiency Gain</div>
            </div>
          </div>

          {/* Challenge section */}
          <div className="bq-content-section">
            <h2 className="bq-section-title">
              <div className="bq-section-icon bq-section-icon-red">
                <TrendingUp size={18} />
              </div>
              The Cloud Cost Challenge
            </h2>
            <p className="bq-section-text">
              If you're spending more on cloud infrastructure than you'd planned — and still don't have clear answers about 
              what's driving that spend — you're not alone. Most fast-growing companies reach a point where cloud bills 
              become unpredictable, cost accountability is unclear, and engineering teams are forced to choose between 
              speed and efficiency.
            </p>
          </div>

          {/* Solution section */}
          <div className="bq-content-section">
            <h2 className="bq-section-title">
              <div className="bq-section-icon bq-section-icon-blue">
                <Zap size={18} />
              </div>
              Our Solution
            </h2>
            <p className="bq-section-text">
              Our FinOps Strategy Services are designed to help you turn cloud spending from a liability into a lever for 
              scale. We help you build a cloud cost operating model that gives your engineering and finance teams shared 
              visibility, shared responsibility, and the power to act.
            </p>
            <p className="bq-section-text">
              We start by understanding your cloud usage patterns, business goals, and technical architecture. From there, 
              we design a cost optimization framework tailored to your environment — whether you're running on AWS, GCP, 
              Azure, or Kubernetes.
            </p>
            <div className="bq-learn-more">
              <span>Learn more about our approach</span>
              <ArrowRight size={16} />
            </div>
          </div>

          {/* Features section */}
          <h2 className="bq-section-title">What You Get</h2>
          <div className="bq-features-container">
            {features.map((feature, index) => (
              <div key={index} className="bq-feature-card">
                <div className="bq-feature-check">
                  <CheckCircle size={16} />
                </div>
                <div className="bq-feature-content">
                  {feature.icon}
                  <div className="bq-feature-text">{feature.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const className = `service-rich-text ${variant === 'highlighted' ? 'highlighted' : ''}`;

  return (
    <section className={className}>
      <div className="service-rich-text-content">
        <div className="content">
          {renderRichText()}
        </div>
      </div>
    </section>
  );
};

export default ServiceRichText; 